import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Calculator, RefreshCw } from 'lucide-react';
import PageHeader from './components/PageHeader.jsx';
import { Button } from '@/components/ui/button.jsx';
import { getGuia } from '@/modules/guias/api/guiasApi.js';
import { valorizarGuia } from '@/services/guiasValorizacion.js';
import { fetchGuideTariffSnapshots } from '@/services/tariffWorkbench.js';
import CreditoOverrideModal from '@/components/creditos/CreditoOverrideModal.jsx';
import { useAuth } from '@/state/auth/AuthContext.jsx';
import { hasPermission } from '@/utils/permissions.js';

const formatCurrency = (value) =>
  new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(Number(value || 0));

const sumBy = (items, key) => items.reduce((acc, item) => acc + Number(item?.[key] || 0), 0);

const formatDateTime = (value) => {
  if (!value) return 'Sin dato';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return 'Sin dato';
  return new Intl.DateTimeFormat('es-AR', { dateStyle: 'medium', timeStyle: 'short' }).format(date);
};

const formatLineaOperativa = ({ sucursalCodigo = '', lineaNumero = '' } = {}) => {
  const codigo = String(sucursalCodigo || '').trim();
  const lineaRaw = String(lineaNumero || '').trim();
  if (!codigo && !lineaRaw) return '';

  const digits = lineaRaw.replace(/\D/g, '');
  const lineaFormateada = digits ? digits.padStart(8, '0') : lineaRaw;
  return codigo ? `${codigo}-${lineaFormateada}` : lineaFormateada;
};

const formatPersonaGuia = (value, fallback = '—') => {
  if (!value) return fallback;
  if (typeof value === 'string' || typeof value === 'number') return String(value);
  if (typeof value !== 'object') return fallback;
  return value.nombre || value.razon_social || value.domicilio || fallback;
};

const flattenSnapshotDetails = (snapshot) => {
  const details = Array.isArray(snapshot?.details) ? snapshot.details : [];
  return details.map((item, index) => ({
    id: item?.id || `${item?.conceptoCodigo || 'concepto'}-${index + 1}`,
    tipo: item?.conceptoCodigo || item?.baseCodigo || 'CONCEPTO',
    codigo: item?.conceptoCodigo || null,
    nombre: item?.conceptoNombre || 'Concepto',
    baseCantidad: Number(item?.cantidad || item?.baseValor || 0),
    valor: Number(item?.valorUnitario || 0),
    minimo: 0,
    importe: Number(item?.importe || 0),
    tipoCalculo: item?.tipoCalculo || 'FIJO',
  }));
};

export default function GuiaValorizar() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { tenant, token, user, sucursal } = useAuth();
  const [detail, setDetail] = useState({ guia: null, remitos: [] });
  const [loading, setLoading] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [manualSaving, setManualSaving] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [snapshotBundle, setSnapshotBundle] = useState(null);
  const [manualImporte, setManualImporte] = useState('');
  const [manualMotivo, setManualMotivo] = useState('');
  const [overrideFacturada, setOverrideFacturada] = useState(false);
  const [overrideMotivo, setOverrideMotivo] = useState('');
  const [creditBlock, setCreditBlock] = useState(null);
  const [sucursalBlock, setSucursalBlock] = useState(null);
  const [pendingOverridePayload, setPendingOverridePayload] = useState(null);
  const [overrideSucursalReason, setOverrideSucursalReason] = useState('');

  const loadDetail = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [payload, snapshots] = await Promise.all([
        getGuia(id),
        fetchGuideTariffSnapshots({ tenant, token, guiaId: id }).catch(() => null),
      ]);
      if (!payload.ok) {
        throw new Error(payload.error || 'No se pudo cargar la guía.');
      }
      setDetail({ guia: payload.guia, remitos: payload.remitos || [] });
      setSnapshotBundle(snapshots);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo cargar la guía.');
    } finally {
      setLoading(false);
    }
  }, [id, tenant, token]);

  useEffect(() => {
    loadDetail();
  }, [loadDetail]);

  const metrics = useMemo(() => {
    if (!detail.guia) return { bultos: 0, kg: 0, mt3: 0 };
    if (detail.remitos.length) {
      return {
        bultos: sumBy(detail.remitos, 'bultos'),
        kg: sumBy(detail.remitos, 'kg_reales'),
        mt3: sumBy(detail.remitos, 'm3'),
      };
    }
    return {
      bultos: Number(detail.guia.bultos || 0),
      kg: Number(detail.guia.kg_reales || 0),
      mt3: Number(detail.guia.m3 || 0),
    };
  }, [detail]);

  const guia = detail.guia;
  const guiaNumero = guia?.numero_guia || guia?.numero || guia?.id || '—';
  const estado = guia?.estado || '—';
  const remitenteNombre = formatPersonaGuia(guia?.remitente, guia?.remitente_nombre || '—');
  const destinatarioNombre = formatPersonaGuia(guia?.destinatario, guia?.destinatario_nombre || '—');
  const currentSnapshot = snapshotBundle?.current || null;
  const snapshotHistory = Array.isArray(snapshotBundle?.headers) ? snapshotBundle.headers : [];
  const eligibility = snapshotBundle?.eligibility || null;
  const downstream = snapshotBundle?.downstream || null;
  const currentBreakdown = useMemo(() => flattenSnapshotDetails(currentSnapshot), [currentSnapshot]);
  const isFacturada = Boolean(guia?.facturada);
  const inFoja = Boolean(guia?.foja_id);
  const inReparto = Boolean(guia?.reparto_id);
  const canValorizar = hasPermission(user, 'GUIAS_REVALORIZAR') || hasPermission(user, 'GUIA_VALORIZAR');
  const canRevalorizar = hasPermission(user, 'GUIAS_REVALORIZAR') || hasPermission(user, 'GUIA_REVALORIZAR');
  const canOverrideCredit = hasPermission(user, 'CREDITO_OVERRIDE');
  const canUseOverride = canRevalorizar;

  const disabledReason = useMemo(() => {
    if (eligibility && !eligibility.allowed) return eligibility.message;
    if (!canValorizar) return 'No tenés permisos para valorizar guías.';
    if (isFacturada && !canUseOverride) return 'No tenés permisos para revalorizar guías facturadas.';
    if (inFoja) return 'La guía está en foja y no puede valorizarse.';
    if (inReparto) return 'La guía está en reparto y no puede valorizarse.';
    if (isFacturada && !overrideFacturada) return 'La guía está facturada. Se requiere override.';
    return '';
  }, [canUseOverride, canValorizar, eligibility, inFoja, inReparto, isFacturada, overrideFacturada]);

  const buildPayload = (tipo, extras = {}) => ({
    tipo,
    sucursal_id: sucursal?.id || guia?.sucursal_actual_id || '',
    usuario_id: user?.id || user?.usuario_id || null,
    override_facturada: overrideFacturada,
    override_motivo: overrideMotivo || undefined,
    ...extras,
  });

  const handleAutoCalcular = async () => {
    if (calculating) return;
    if (disabledReason) {
      setError(disabledReason);
      return;
    }

    setCalculating(true);
    setError('');
    setCreditBlock(null);
    setSucursalBlock(null);
    try {
      const payload = buildPayload('auto');
      const data = await valorizarGuia({ tenant, token, guiaId: id, payload });
      setResult(data);
      await loadDetail();
    } catch (err) {
      const payload = err?.payload || {};
      if (payload?.code === 'BLOQUEO_SUCURSAL_ORIGEN') {
        setSucursalBlock(payload);
        return;
      }
      if (payload?.code === 'CREDITO_BLOQUEADO' || payload?.error === 'CREDITO_BLOQUEADO') {
        setCreditBlock(payload);
        setPendingOverridePayload(buildPayload('auto'));
        return;
      }
      setError(err instanceof Error ? err.message : 'No se pudo calcular la valorización.');
    } finally {
      setCalculating(false);
    }
  };

  const handleManual = async () => {
    if (manualSaving) return;
    if (disabledReason) {
      setError(disabledReason);
      return;
    }
    if (!manualMotivo.trim()) {
      setError('El motivo es obligatorio para el ajuste manual.');
      return;
    }

    setManualSaving(true);
    setError('');
    setCreditBlock(null);
    setSucursalBlock(null);
    try {
      const payload = buildPayload('manual', {
        importe: Number(manualImporte || 0),
        motivo: manualMotivo.trim(),
      });
      const data = await valorizarGuia({ tenant, token, guiaId: id, payload });
      setResult(data);
      await loadDetail();
    } catch (err) {
      const payload = err?.payload || {};
      if (payload?.code === 'BLOQUEO_SUCURSAL_ORIGEN') {
        setSucursalBlock(payload);
        return;
      }
      if (payload?.code === 'CREDITO_BLOQUEADO' || payload?.error === 'CREDITO_BLOQUEADO') {
        setCreditBlock(payload);
        setPendingOverridePayload(
          buildPayload('manual', {
            importe: Number(manualImporte || 0),
            motivo: manualMotivo.trim(),
          }),
        );
        return;
      }
      setError(err instanceof Error ? err.message : 'No se pudo guardar el ajuste manual.');
    } finally {
      setManualSaving(false);
    }
  };

  const handleOverrideCredit = async (motivo) => {
    if (calculating || manualSaving) return;
    setError('');
    try {
      const basePayload = pendingOverridePayload || buildPayload('auto');
      const payload = {
        ...basePayload,
        credit_override: true,
        credit_override_motivo: motivo,
      };
      const data = await valorizarGuia({ tenant, token, guiaId: id, payload });
      setResult(data);
      await loadDetail();
      setCreditBlock(null);
      setPendingOverridePayload(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo aplicar el override de crédito.');
    }
  };

  const handleSucursalOverride = async () => {
    if (!sucursalBlock?.allow_override) return;
    const motivo = overrideSucursalReason.trim();
    if (!motivo) {
      setError('Debe indicar el motivo del override de sucursal.');
      return;
    }
    setCalculating(true);
    setError('');
    try {
      const payload = buildPayload('auto', { override_reason: motivo });
      const data = await valorizarGuia({ tenant, token, guiaId: id, payload });
      setResult(data);
      await loadDetail();
      setSucursalBlock(null);
      setOverrideSucursalReason('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo ejecutar el override de sucursal.');
    } finally {
      setCalculating(false);
    }
  };

  return (
    <div className="min-h-screen bg-[rgba(var(--bg))]">
      <div className="mx-auto max-w-[1400px] px-6 pt-6">
        <Button variant="ghost" onClick={() => navigate(-1)} className="gap-2">
          <ArrowLeft size={16} aria-hidden="true" /> Volver
        </Button>
      </div>

      <section className="mx-auto max-w-[1400px] space-y-6 px-6 pb-10">
        <PageHeader
          title={`Valorización de guía ${guiaNumero}`}
          description="Calculá automáticamente con tarifas o ajustá manualmente con auditoría obligatoria."
          breadcrumbs={[
            { label: 'Guías', href: '/guias' },
            { label: guiaNumero },
            { label: 'Valorización' },
          ]}
          actions={
            <Button variant="outline" onClick={loadDetail} className="gap-2">
              <RefreshCw size={16} aria-hidden="true" /> Refrescar
            </Button>
          }
        />

        {loading ? (
          <div className="rounded-2xl border border-[rgba(var(--border),_0.4)] bg-[rgba(var(--panel),_0.6)] p-6 text-sm text-[rgba(var(--fg),_0.65)]">
            Cargando guía...
          </div>
        ) : !guia ? (
          <div className="rounded-2xl border border-[rgba(var(--border),_0.4)] bg-[rgba(var(--panel),_0.6)] p-6 text-sm text-[rgba(var(--fg),_0.65)]">
            No se encontró la guía solicitada.
          </div>
        ) : (
          <>
            <div className="grid gap-4 lg:grid-cols-3">
              <div className="rounded-2xl border border-[rgba(var(--border),_0.4)] bg-[rgba(var(--panel),_0.6)] p-5">
                <h3 className="text-sm font-semibold uppercase text-[rgba(var(--fg),_0.6)]">Datos operativos</h3>
                <div className="mt-3 space-y-2 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-[rgba(var(--fg),_0.6)]">Estado</span>
                    <span className="font-semibold text-[rgb(var(--fg))]">{estado}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-[rgba(var(--fg),_0.6)]">Remitente</span>
                    <span className="font-semibold text-[rgb(var(--fg))]">{remitenteNombre}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-[rgba(var(--fg),_0.6)]">Destinatario</span>
                    <span className="font-semibold text-[rgb(var(--fg))]">{destinatarioNombre}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-[rgba(var(--fg),_0.6)]">Línea</span>
                    <span className="font-semibold text-[rgb(var(--fg))]">
                      {guia.linea_operativa || formatLineaOperativa({
                        sucursalCodigo: guia.sucursal_origen_codigo || guia.sucursal_actual_codigo || guia.sucursal_destino_codigo || guia.codigo_sucursal,
                        lineaNumero: guia.linea_numero ?? guia.linea ?? '',
                      }) || 'Sin dato registrado'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-[rgba(var(--fg),_0.6)]">Sucursal actual</span>
                    <span className="font-semibold text-[rgb(var(--fg))]">{guia.sucursal_actual_id ?? '—'}</span>
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border border-[rgba(var(--border),_0.4)] bg-[rgba(var(--panel),_0.6)] p-5">
                <h3 className="text-sm font-semibold uppercase text-[rgba(var(--fg),_0.6)]">Datos de carga</h3>
                <div className="mt-3 space-y-2 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-[rgba(var(--fg),_0.6)]">Bultos</span>
                    <span className="font-semibold text-[rgb(var(--fg))]">{metrics.bultos.toFixed(0)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-[rgba(var(--fg),_0.6)]">Kg reales</span>
                    <span className="font-semibold text-[rgb(var(--fg))]">{metrics.kg.toFixed(2)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-[rgba(var(--fg),_0.6)]">M³</span>
                    <span className="font-semibold text-[rgb(var(--fg))]">{metrics.mt3.toFixed(3)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-[rgba(var(--fg),_0.6)]">Valorizada</span>
                    <span className="font-semibold text-[rgb(var(--fg))]">{guia.valorizada ? 'Sí' : 'No'}</span>
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border border-[rgba(var(--border),_0.4)] bg-[rgba(var(--panel),_0.6)] p-5">
                <h3 className="text-sm font-semibold uppercase text-[rgba(var(--fg),_0.6)]">Reglas críticas</h3>
                <ul className="mt-3 space-y-2 text-sm text-[rgba(var(--fg),_0.7)]">
                  <li>• No valorizar guías en foja o reparto.</li>
                  <li>• No revalorizar guías facturadas (salvo override).</li>
                  <li>• Todo cambio queda auditado en historial.</li>
                </ul>
                {canUseOverride ? (
                  <>
                    <label className="mt-4 flex items-center gap-2 text-sm text-[rgba(var(--fg),_0.7)]">
                      <input
                        type="checkbox"
                        checked={overrideFacturada}
                        onChange={(event) => setOverrideFacturada(event.target.checked)}
                        className="h-4 w-4 accent-[rgb(var(--brand))]"
                      />
                      Override gerencial para guía facturada
                    </label>
                    {overrideFacturada ? (
                      <textarea
                        value={overrideMotivo}
                        onChange={(event) => setOverrideMotivo(event.target.value)}
                        rows={3}
                        placeholder="Motivo del override"
                        className="mt-2 w-full rounded-xl border border-[rgba(var(--border),_0.5)] bg-transparent px-3 py-2 text-sm"
                      />
                    ) : null}
                  </>
                ) : (
                  <p className="mt-4 text-xs text-[rgba(var(--danger,255_99_99),_0.9)]">
                    No tenés permisos para revalorizar guías facturadas.
                  </p>
                )}
              </div>
            </div>

            <div className="grid gap-4 lg:grid-cols-3">
              <div className="rounded-2xl border border-[rgba(var(--border),_0.4)] bg-[rgba(var(--panel),_0.6)] p-5">
                <h3 className="text-sm font-semibold uppercase text-[rgba(var(--fg),_0.6)]">Snapshot vigente</h3>
                <div className="mt-3 space-y-2 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-[rgba(var(--fg),_0.6)]">Origen</span>
                    <span className="font-semibold text-[rgb(var(--fg))]">{currentSnapshot?.source || 'Sin dato'}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-[rgba(var(--fg),_0.6)]">Fecha</span>
                    <span className="font-semibold text-[rgb(var(--fg))]">{formatDateTime(currentSnapshot?.createdAt || guia?.valorizacion_snapshot_at)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-[rgba(var(--fg),_0.6)]">Usuario</span>
                    <span className="font-semibold text-[rgb(var(--fg))]">{currentSnapshot?.createdBy || 'Sin dato'}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-[rgba(var(--fg),_0.6)]">Total actual</span>
                    <span className="font-semibold text-[rgb(var(--brand))]">{formatCurrency(currentSnapshot?.totalFinal || guia?.total_valorizado || guia?.total || 0)}</span>
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border border-[rgba(var(--border),_0.4)] bg-[rgba(var(--panel),_0.6)] p-5">
                <h3 className="text-sm font-semibold uppercase text-[rgba(var(--fg),_0.6)]">Historial</h3>
                {snapshotHistory.length ? (
                  <div className="mt-3 space-y-2 text-sm">
                    {snapshotHistory.slice(0, 4).map((header) => (
                      <div key={header.id} className="rounded-xl border border-[rgba(var(--border),_0.35)] bg-[rgba(var(--panel),_0.72)] px-3 py-2">
                        <div className="flex items-center justify-between gap-3">
                          <span className="font-semibold text-[rgb(var(--fg))]">{header.source}</span>
                          <span className="text-[rgba(var(--fg),_0.62)]">{formatDateTime(header.createdAt)}</span>
                        </div>
                        <div className="mt-1 flex items-center justify-between gap-3 text-[rgba(var(--fg),_0.72)]">
                          <span>{header.estado}</span>
                          <strong className="text-[rgb(var(--fg))]">{formatCurrency(header.totalFinal)}</strong>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="mt-3 rounded-xl border border-dashed border-[rgba(var(--border),_0.45)] p-4 text-sm text-[rgba(var(--fg),_0.62)]">
                    Todavía no hay snapshots relacionales publicados para esta guía.
                  </div>
                )}
              </div>

              <div className="rounded-2xl border border-[rgba(var(--border),_0.4)] bg-[rgba(var(--panel),_0.6)] p-5">
                <h3 className="text-sm font-semibold uppercase text-[rgba(var(--fg),_0.6)]">Bloqueos legacy</h3>
                {eligibility?.allowed ? (
                  <p className="mt-3 text-sm text-emerald-700">La guía sigue en estado emitida y no tiene consumos posteriores detectados.</p>
                ) : (
                  <div className="mt-3 space-y-2 text-sm text-[rgba(var(--fg),_0.72)]">
                    {(eligibility?.reasons || []).slice(0, 3).map((reason) => (
                      <div key={reason} className="rounded-xl border border-amber-500/25 bg-amber-500/10 px-3 py-2">
                        {reason}
                      </div>
                    ))}
                  </div>
                )}
                <div className="mt-4 space-y-2 text-xs text-[rgba(var(--fg),_0.62)]">
                  <p>Facturación vinculada: <strong>{Number(downstream?.facturacion?.count || 0)}</strong></p>
                  <p>Cobranzas aplicadas: <strong>{Number(downstream?.cobranza?.count || 0)}</strong></p>
                  <p>Total cobrado/aplicado: <strong>{formatCurrency(downstream?.cobranza?.totalAplicado || 0)}</strong></p>
                </div>
              </div>
            </div>

            <div className="grid gap-6 lg:grid-cols-[1.2fr_0.8fr]">
              <div className="rounded-2xl border border-[rgba(var(--border),_0.4)] bg-[rgba(var(--panel),_0.6)] p-5">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <h3 className="text-lg font-semibold text-[rgb(var(--fg))]">Cálculo automático</h3>
                    <p className="text-sm text-[rgba(var(--fg),_0.6)]">
                      Aplica tarifas configuradas por cliente o línea y respeta mínimos.
                    </p>
                  </div>
                  {canValorizar ? (
                    <Button onClick={handleAutoCalcular} className="gap-2" disabled={calculating || Boolean(disabledReason)}>
                      <Calculator size={16} aria-hidden="true" />
                      {calculating ? 'Calculando...' : 'Calcular con tarifas'}
                    </Button>
                  ) : null}
                </div>

                {(result?.breakdown?.length || currentBreakdown.length) ? (
                  <div className="mt-5 overflow-x-auto">
                    <table className="min-w-full border-separate border-spacing-y-2 text-sm">
                      <thead>
                        <tr className="text-left text-xs uppercase text-[rgba(var(--fg),_0.6)]">
                          <th className="px-3">Tipo</th>
                          <th className="px-3">Base</th>
                          <th className="px-3">Valor</th>
                          <th className="px-3">Mínimo</th>
                          <th className="px-3">Importe</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(result?.breakdown?.length ? result.breakdown : currentBreakdown).map((item) => (
                          <tr
                            key={item.id || `${item.codigo || item.nombre}-${item.tipo}`}
                            className="rounded-xl border border-[rgba(var(--border),_0.3)] bg-[rgba(var(--panel),_0.8)]"
                          >
                            <td className="px-3 py-3 uppercase">{item.codigo || item.tipo}</td>
                            <td className="px-3 py-3">{item.baseCantidad}</td>
                            <td className="px-3 py-3">{formatCurrency(item.valor)}</td>
                            <td className="px-3 py-3">{formatCurrency(item.minimo || 0)}</td>
                            <td className="px-3 py-3 font-semibold">{formatCurrency(item.importe)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="mt-5 rounded-xl border border-dashed border-[rgba(var(--border),_0.5)] p-6 text-center text-sm text-[rgba(var(--fg),_0.6)]">
                    Ejecutá el cálculo para ver el desglose de tarifas.
                  </div>
                )}
              </div>

              <div className="space-y-6">
                <div className="rounded-2xl border border-[rgba(var(--border),_0.4)] bg-[rgba(var(--panel),_0.6)] p-5">
                  <h3 className="text-lg font-semibold text-[rgb(var(--fg))]">Ajuste manual</h3>
                  <p className="mt-2 text-xs text-[rgba(var(--fg),_0.55)]">
                    TODO LEGACY-PENDING: validar en `ST338.PRG` si el override manual comercial existe con esta semántica o si debe restringirse a procesos posteriores.
                  </p>
                  <p className="text-sm text-[rgba(var(--fg),_0.6)]">
                    Ingresá un importe manual y justificá el motivo para auditoría.
                  </p>
                  <div className="mt-4 space-y-3">
                    <label className="block text-sm">
                      <span className="text-xs font-semibold uppercase text-[rgba(var(--fg),_0.6)]">Importe</span>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={manualImporte}
                        onChange={(event) => setManualImporte(event.target.value)}
                        className="mt-1 w-full rounded-xl border border-[rgba(var(--border),_0.5)] bg-transparent px-3 py-2 text-sm"
                      />
                    </label>
                    <label className="block text-sm">
                      <span className="text-xs font-semibold uppercase text-[rgba(var(--fg),_0.6)]">Motivo</span>
                      <textarea
                        rows={4}
                        value={manualMotivo}
                        onChange={(event) => setManualMotivo(event.target.value)}
                        placeholder="Detalle del ajuste manual"
                        className="mt-1 w-full rounded-xl border border-[rgba(var(--border),_0.5)] bg-transparent px-3 py-2 text-sm"
                      />
                    </label>
                    {canValorizar ? (
                      <Button onClick={handleManual} disabled={manualSaving || Boolean(disabledReason)} className="w-full">
                        {manualSaving ? 'Guardando...' : 'Guardar ajuste manual'}
                      </Button>
                    ) : null}
                  </div>
                </div>

                <div className="rounded-2xl border border-[rgba(var(--border),_0.4)] bg-[rgba(var(--panel),_0.6)] p-5">
                  <h3 className="text-lg font-semibold text-[rgb(var(--fg))]">Resultado</h3>
                  <div className="mt-4 space-y-2 text-sm">
                    <div className="flex items-center justify-between">
                      <span className="text-[rgba(var(--fg),_0.6)]">Neto</span>
                      <span className="font-semibold text-[rgb(var(--fg))]">{formatCurrency(result?.neto || currentSnapshot?.totalNeto || 0)}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-[rgba(var(--fg),_0.6)]">IVA</span>
                      <span className="font-semibold text-[rgb(var(--fg))]">{formatCurrency(result?.iva || currentSnapshot?.totalImpuestos || 0)}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-[rgba(var(--fg),_0.6)]">Total</span>
                      <span className="text-lg font-semibold text-[rgb(var(--brand))]">
                        {formatCurrency(result?.total || currentSnapshot?.totalFinal || guia?.total_valorizado || 0)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {creditBlock ? (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
                Crédito bloqueado. Revisá los límites o solicitá override.
              </div>
            ) : null}
            {sucursalBlock ? (
              <div className="rounded-2xl border border-amber-500/40 bg-amber-500/10 p-4 text-sm text-amber-700 dark:text-amber-300">
                <p className="font-medium">Documento bloqueado por sucursal origen hasta recepción en destino.</p>
                {sucursalBlock.allow_override ? (
                  <div className="mt-3 space-y-2">
                    <textarea
                      value={overrideSucursalReason}
                      onChange={(event) => setOverrideSucursalReason(event.target.value)}
                      rows={3}
                      placeholder="Motivo obligatorio del override"
                      className="w-full rounded-xl border border-amber-500/30 bg-transparent px-3 py-2 text-sm"
                    />
                    <Button type="button" variant="outline" onClick={handleSucursalOverride} disabled={calculating}>
                      Override
                    </Button>
                  </div>
                ) : null}
              </div>
            ) : null}
            {error ? (
              <div className="rounded-2xl border border-[rgba(var(--danger,255_99_99),_0.4)] bg-[rgba(var(--danger,255_99_99),_0.15)] p-4 text-sm text-[rgb(var(--danger,255_99_99))]">
                {error}
              </div>
            ) : null}
            {disabledReason ? (
              <div className="rounded-2xl border border-[rgba(var(--warning,245_158_11),_0.4)] bg-[rgba(var(--warning,245_158_11),_0.15)] p-4 text-sm text-[rgb(var(--warning,245_158_11))]">
                {disabledReason}
              </div>
            ) : null}
          </>
        )}
      </section>
      <CreditoOverrideModal
        isOpen={Boolean(creditBlock)}
        onClose={() => {
          setCreditBlock(null);
          setPendingOverridePayload(null);
        }}
        credit={creditBlock?.credit}
        canOverride={canOverrideCredit && creditBlock?.allow_override}
        onConfirm={handleOverrideCredit}
      />
    </div>
  );
}
